import { Controller } from "@hotwired/stimulus"
import { startIcons } from "../import_lucid.js"

// The ask_user card under an answer (_ask_card): one question at a time, an option or "Something else"
// each. Continue on the last one sends the answers through the composer as the user's next message,
// with hidden fields that keep them on the asking message (ChatsController#record_ask_answers), and the
// card stays as the answered, read-only version. Skip leaves a question unanswered. A message typed in
// the composer instead retires the card.
export default class extends Controller {
  static targets = ["step", "pos", "prev", "next", "go"]

  connect() {
    startIcons(this.element) // broadcast in after the page's icons ran
    if (this.isDone) return

    this.index = 0
    this.retire = (e) => { if (!this.isDone && e.target === this.input?.form) this.dismiss() }
    document.addEventListener("submit", this.retire)
    this.show()
  }

  disconnect() { if (this.retire) document.removeEventListener("submit", this.retire) }

  picked() { // an option clears what was typed
    this.current.querySelector(".laz-ask-other").value = ""
    this.show()
  }

  typed(event) { // typing clears the option
    if (event.target.value.trim()) this.current.querySelectorAll("input[type=radio]").forEach((r) => { r.checked = false })
    this.show()
  }

  enter(event) {
    event.preventDefault()
    if (this.answer(this.current)) this.go()
  }

  prev() { this.move(-1) }
  next() { this.move(1) }

  skip() {
    this.current.querySelectorAll("input[type=radio]").forEach((r) => { r.checked = false })
    this.current.querySelector(".laz-ask-other").value = ""
    this.go()
  }

  go() {
    if (this.index < this.stepTargets.length - 1) return this.move(1)

    const answers  = this.stepTargets.map((s) => this.answer(s) || null)
    const answered = this.stepTargets.map((s, i) => [s.dataset.question, answers[i]]).filter(([, a]) => a)
    const input = this.input
    if (!answered.length || !input?.form) return this.dismiss()

    // Each pick after its question, so the model reads what it answers in the message itself.
    input.value = answered.map(([q, a]) => `${q} ${a}`).join("\n")
    this.field(input.form, "ask_message_id", this.element.dataset.messageId)
    this.field(input.form, "ask_answers", JSON.stringify(answers))
    this.done(answers)
    input.form.requestSubmit()
  }

  // The read-only version _ask_card renders once other_data['ask_answers'] is set.
  done(answers) {
    this.element.classList.add("is-done")
    this.stepTargets.forEach((s, i) => {
      s.hidden = false
      if (!answers[i]) s.dataset.skipped = ""
    })
    this.element.querySelectorAll("input").forEach((i) => { i.disabled = true })
  }

  field(form, name, value) {
    const el = form.querySelector(`input[type=hidden][name="${name}"]`) ||
      form.appendChild(Object.assign(document.createElement("input"), { type: "hidden", name }))
    el.value = value
  }

  // The composer this card answers through: the Platform app panel's own, else PharOS's #chat-input.
  get input() {
    return this.element.closest(".lzc-body")?.querySelector("textarea[name=content]") || document.getElementById("chat-input")
  }

  get isDone() { return this.element.classList.contains("is-done") }

  dismiss() { this.element.remove() }

  answer(step) {
    return step.querySelector(".laz-ask-other").value.trim() || step.querySelector("input[type=radio]:checked")?.value || ""
  }

  move(by) {
    this.index = Math.min(Math.max(this.index + by, 0), this.stepTargets.length - 1)
    this.show()
  }

  get current() { return this.stepTargets[this.index] }

  show() {
    const last = this.stepTargets.length - 1
    this.stepTargets.forEach((s, i) => { s.hidden = i !== this.index })
    if (this.hasPosTarget) this.posTarget.textContent = `${this.index + 1}/${last + 1}`
    if (this.hasPrevTarget) this.prevTarget.disabled = this.index === 0
    if (this.hasNextTarget) this.nextTarget.disabled = this.index === last
    this.goTarget.disabled = !this.answer(this.current)
  }
}
